<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { formatDate, formatMoney } from '../helpers';

const props = defineProps({
  deal: { type: Object, required: true },
});

const emit = defineEmits(['open', 'toggle-favorite']);

const { t, locale } = useI18n();

const amount = computed(() =>
  formatMoney(props.deal.amountCents, props.deal.currency, locale.value)
);

const isOverdue = computed(() => {
  if (!props.deal.expectedCloseDate || props.deal.closedAt) return false;
  // parse as LOCAL midnight (bare ISO dates parse as UTC) and only flag past days
  const due = new Date(`${props.deal.expectedCloseDate}T23:59:59`);
  return due < new Date();
});

const closeDate = computed(() => {
  if (!props.deal.expectedCloseDate) return '';
  return formatDate(props.deal.expectedCloseDate, locale.value);
});
</script>

<template>
  <button
    type="button"
    class="group/card flex flex-col w-full gap-2 p-3 text-left rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak hover:outline-n-slate-7 transition-all cursor-grab active:cursor-grabbing"
    @click="emit('open', deal)"
  >
    <div class="flex items-start justify-between w-full gap-2">
      <span class="text-sm font-medium leading-5 text-n-slate-12 line-clamp-2">
        {{ deal.name }}
      </span>
      <span
        role="button"
        tabindex="0"
        :aria-label="t('WASAPI_CRM.FAVORITE.TOGGLE')"
        class="grid shrink-0 place-content-center size-5 rounded hover:bg-n-alpha-2"
        :class="
          deal.isFavorite
            ? 'text-n-amber-10'
            : 'text-n-slate-8 opacity-0 group-hover/card:opacity-100'
        "
        @click.stop="emit('toggle-favorite', deal)"
        @keydown.enter.stop="emit('toggle-favorite', deal)"
      >
        <Icon
          :icon="deal.isFavorite ? 'i-lucide-star' : 'i-lucide-star-off'"
          class="size-4"
        />
      </span>
      <Avatar
        v-if="deal.assignee"
        v-tooltip="deal.assignee.name"
        :name="deal.assignee.name"
        :src="deal.assignee.thumbnail"
        :size="20"
        rounded-full
      />
    </div>
    <span v-if="deal.amountCents" class="text-sm font-semibold text-n-teal-11">
      {{ amount }}
    </span>
    <div
      v-if="deal.contact"
      class="flex items-center gap-1.5 text-xs text-n-slate-11 truncate"
    >
      <Icon icon="i-lucide-user" class="size-3.5 shrink-0" />
      <span class="truncate">{{ deal.contact.name }}</span>
    </div>
    <div
      v-if="closeDate"
      class="flex items-center gap-1.5 text-xs"
      :class="isOverdue ? 'text-n-ruby-11' : 'text-n-slate-11'"
    >
      <Icon icon="i-lucide-calendar" class="size-3.5 shrink-0" />
      <span>{{ closeDate }}</span>
      <span v-if="isOverdue">· {{ t('WASAPI_CRM.DEAL.OVERDUE') }}</span>
    </div>
  </button>
</template>
